import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bus, 
  Search, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Building2, 
  HelpCircle, 
  ChevronDown, 
  Compass
} from 'lucide-react';
import SEO from '../components/SEO';
import BusDisclaimer from '../components/BusDisclaimer';
import rawData from '../data/busTimeTableData.json';
import { HINDI_PLACES } from '../config/busConfig';
import { SITE_URL } from '../config/seo';

export default function BusTimeTableVillageHub() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Filter originating from Fatehabad to destinations with < 5 buses
  const villageBuses = useMemo(() => {
    const ftbDirect = rawData.timetable.filter(
      (r) => r.bus_stand === 'Fatehabad' && r.from === 'Fatehabad'
    );
    // count by dest
    const destCounts = {};
    ftbDirect.forEach((r) => {
      destCounts[r.to] = (destCounts[r.to] || 0) + 1;
    });

    return ftbDirect.filter((r) => destCounts[r.to] < 5)
      .sort((a, b) => a.to.localeCompare(b.to) || a.departure_time.localeCompare(b.departure_time));
  }, []);

  // Unique village destinations grouped
  const groupedVillages = useMemo(() => {
    const map = {};
    villageBuses.forEach((b) => {
      if (!map[b.to]) map[b.to] = [];
      map[b.to].push(b);
    });
    return map;
  }, [villageBuses]);

  const filteredVillages = useMemo(() => {
    if (!searchTerm.trim()) return groupedVillages;
    const q = searchTerm.toLowerCase();
    const result = {};
    Object.keys(groupedVillages).forEach((dest) => {
      const buses = groupedVillages[dest];
      const matchDest = dest.toLowerCase().includes(q);
      const matchHindi = (HINDI_PLACES[dest] || '').toLowerCase().includes(q);
      const matchBus = buses.some(
        (b) => b.via.toLowerCase().includes(q) || b.departure_time.includes(q)
      );
      if (matchDest || matchHindi || matchBus) {
        result[dest] = buses;
      }
    });
    return result;
  }, [groupedVillages, searchTerm]);

  const pageTitle = 'Fatehabad Village Bus Time Table 2026 - Haryana Roadways';
  const metaDesc = 'Fatehabad to village routes bus time table 2026: 61 daily buses across 32 rural destinations. Check official departure timings, stops and enquiry information.';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Bus Time Table',
        item: `${SITE_URL}/bus-time-table/`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Fatehabad Village Routes Time Table',
        item: `${SITE_URL}/bus-time-table/fatehabad-village-routes/`
      }
    ]
  };

  const faqData = [
    {
      q: 'फतेहाबाद से किन-किन गांवों के लिए हरियाणा रोडवेज बसें चलती हैं?',
      a: 'फतेहाबाद बस स्टैंड से ढांगर, खैरमपुर, ढारनियां, बनावली, भट्टू कलां, बीघड़, नागपुर, अहैरवां, फूला, नूरकी, हंसगा, झालनियां, बड़सीन, माजरा, भोड़िया खेड़ा, ढाणी ढाका, पलसर, बीराबादी, सबरवास, करणोली, अहलीसदर, अग्रोहा आदि 32 से अधिक ग्रामीण क्षेत्रों के लिए सीधी बस सेवाएं उपलब्ध हैं।'
    },
    {
      q: 'ग्रामीण रूटों की बसें किस समय चलती हैं?',
      a: 'ग्रामीण रूटों की पहली बसें प्रायः सुबह 07:00 बजे से 08:30 बजे के बीच तथा दोपहर और शाम 17:00 से 18:30 बजे के मध्य संचालित होती हैं।'
    },
    {
      q: 'क्या ग्रामीण रूटों पर निजी या सरकारी दोनों बसें चलती हैं?',
      a: 'हाँ, ग्रामीण रूटों पर हरियाणा रोडवेज (HR) तथा अधिकृत सहकारी समितियां (PVT) दोनों समय सारिणी के अनुसार सेवा प्रदान करती हैं।'
    }
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a
      }
    }))
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: metaDesc,
    url: `${SITE_URL}/bus-time-table/fatehabad-village-routes/`,
    datePublished: '2026-10-18',
    dateModified: '2026-10-18',
    inLanguage: ['hi', 'en']
  };

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-gray-950 text-gray-800 dark:text-gray-200">
      <SEO
        title={pageTitle}
        titleTemplate=""
        description={metaDesc}
        canonical="/bus-time-table/fatehabad-village-routes/"
        jsonLd={[breadcrumbJsonLd, faqJsonLd, webPageJsonLd]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <ol className="flex items-center gap-1.5 sm:gap-2">
            <li>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/bus-time-table" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Bus Time Table
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-gray-900 dark:text-gray-100">
              Fatehabad Village Routes
            </li>
          </ol>
        </nav>

        <div className="bg-gradient-to-br from-amber-900 via-orange-950 to-slate-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-semibold uppercase tracking-wider border border-amber-400/30">
              <Compass className="h-3.5 w-3.5" />
              Rural & Village Bus Network – Fatehabad Depot
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Fatehabad to Village & Rural Routes Bus Time Table 2026 (फतेहाबाद से ग्रामीण बस समय सारिणी)
            </h1>
            <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed">
              फतेहाबाद बस स्टैंड से चलने वाली सभी 32 ग्रामीण व कस्बाई डेस्टिनेशंस की 61 दैनिक बसों की संपूर्ण समय सारिणी। अपने गांव की बस का समय, रूट via और ऑपरेटर देखें।
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2 text-xs sm:text-sm">
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🌾 32 Village Destinations
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🚌 61 Daily Departures
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                ⏱️ Sorted Chronologically
              </span>
            </div>
          </div>
        </div>

        <BusDisclaimer />

        {/* Search Bar */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 shadow-sm">
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search your village (गांव खोजें): e.g. Dhanger, Bhattu, Nagpur, Bighar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 text-sm sm:text-base rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Grouped Village Tables */}
        <div className="space-y-6">
          {Object.keys(filteredVillages).length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400">
              कोई ग्रामीण रूट नहीं मिला। कृपया दूसरा नाम खोजें।
            </div>
          ) : (
            Object.keys(filteredVillages).map((dest) => {
              const buses = filteredVillages[dest];
              const hiName = HINDI_PLACES[dest] || dest;

              return (
                <div
                  key={dest}
                  className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden"
                >
                  <div className="p-4 sm:p-5 bg-slate-50/80 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <div>
                      <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-amber-600" />
                        Fatehabad to {dest} ({hiName})
                      </h2>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {buses.length} Daily Bus Service{buses.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="text-xs font-mono font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-2.5 py-1 rounded-md border border-amber-200 dark:border-amber-900">
                      Departs: {buses.map((b) => b.departure_time).join(', ')}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs sm:text-sm">
                      <caption className="sr-only">Bus timings from Fatehabad to {dest}</caption>
                      <thead className="bg-slate-50/40 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 font-semibold">
                        <tr>
                          <th scope="col" className="py-2.5 px-4 w-12 text-center">#</th>
                          <th scope="col" className="py-2.5 px-4">Departure Time</th>
                          <th scope="col" className="py-2.5 px-4">Via Route</th>
                          <th scope="col" className="py-2.5 px-4">Operator</th>
                          <th scope="col" className="py-2.5 px-4">Service Type</th>
                          <th scope="col" className="py-2.5 px-4 text-center">Days</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {buses.map((b, idx) => (
                          <tr key={`${b.serial_no}-${idx}`} className="hover:bg-amber-50/30 dark:hover:bg-amber-950/10 transition-colors">
                            <td className="py-2 px-4 text-center font-mono text-gray-400">{idx + 1}</td>
                            <td className="py-2 px-4 font-mono font-bold text-sm text-gray-900 dark:text-white">
                              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 rounded">
                                <Clock className="h-3 w-3" />
                                {b.departure_time}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{b.via}</td>
                            <td className="py-2 px-4 font-bold text-xs">{b.operator}</td>
                            <td className="py-2 px-4">
                              <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300">
                                {b.service_type}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-center text-xs text-gray-500">{b.service_days || 'All Days'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FAQs */}
        <section aria-labelledby="village-faqs-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-4">
            <HelpCircle className="h-6 w-6 text-amber-600" />
            <h2 id="village-faqs-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Fatehabad Village Routes FAQs (अक्सर पूछे जाने वाले सवाल)
            </h2>
          </div>

          <div className="space-y-3">
            {faqData.map((f, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-900 dark:text-white hover:bg-slate-50 dark:hover:bg-gray-800/50 transition gap-3"
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180 text-amber-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/30">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
